'use client';

import { cn } from '@ahm/common-helpers';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import BannerSection from '../../(main)/store/components/store-banner-section';
import InlineLoadingSkeletons from './inline-loading-skeletons';
import StoreCard from './store-card';
import StoreEmptyState from './store-empty-state';
import StoreList from './store-list-client';
import type { Store } from '@/api/ahafood';
import { DEFAULT_STORE_RADIUS, getStoresFn } from '@/api/ahafood/get-store-fn';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import {
  normalizeLocationKey,
  createStoreQueryKey,
} from '@/lib/query-key-utils';
import { useAiFoodOrderStore } from '@/store/use-ai-store';

export type Payload = {
  city_id: string;
  category_slug?: string;
  promotion?: boolean;
  freeship?: 'freeship' | 'freeship-100' | false;
  special_request?: string;
  hot_deal?: boolean;
  campaign_id?: string;
  text?: string;
  lat?: number;
  lng?: number;
  radius?: number;
  open_now?: boolean;
  item_tag?: string;
};

export type StoreListingProps = {
  data: Store[];
  payload: Payload;
  processedDeals?: Record<string, any>;
};

// Tách logic tính toán banner ra ngoài component
const getBannerInterval = (isMobile: boolean, screenWidth?: number) => {
  if (isMobile) return 5;
  if (screenWidth && screenWidth > 1023) return 8;
  return 3;
};

const shouldShowBannerAtIndex = (index: number, interval: number) => {
  return (index + 1) % interval === 0 && (interval !== 8 || index !== 0);
};

const getBannerData = (
  index: number,
  interval: number,
  bannerKeys: string[],
  processedDeals: Record<string, any>
) => {
  const bannerIndex = Math.floor(index / interval);
  const bannerKey = bannerKeys[bannerIndex];
  return bannerKey ? processedDeals[bannerKey] : null;
};

export default function SimpleStoreList({
  data,
  payload,
  processedDeals,
}: StoreListingProps) {
  const isMobile = useIsMobile();
  const address = useAiFoodOrderStore.use.address();
  const lat = address?.lat;
  const lng = address?.lng;
  const hasLocation = lat != null && lng != null;
  const limit = 20;
  const queryClient = useQueryClient();

  // Check if we're in webinapp mode
  const isWebinapp = useDetectWebInApp();

  // Track previous geohash region to detect meaningful location changes
  const prevGeohash = useRef<string>();

  // Surgical cache invalidation: only invalidate when moving to a DIFFERENT geohash region (~1.2km away)
  // This prevents unnecessary refetches on minor coordinate changes
  useEffect(() => {
    if (!isWebinapp || !lat || !lng) return;

    const currentGeohash = normalizeLocationKey(lat, lng, 6);

    // Only invalidate if we've moved to a different geohash region
    if (prevGeohash.current && prevGeohash.current !== currentGeohash) {
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'stores' &&
          (query.queryKey[1] as { geohash?: string } | undefined)?.geohash ===
            prevGeohash.current,
      });
    }

    prevGeohash.current = currentGeohash;
  }, [lat, lng, isWebinapp, queryClient]);

  // Setup infinite query for fetching more stores
  const {
    data: storesData,
    fetchNextPage,
    hasNextPage,
    isPending,
    isFetched,
  } = useInfiniteQuery({
    queryKey: createStoreQueryKey({ ...payload, lat, lng }), // Use geohash-normalized key for better caching
    queryFn: async ({ pageParam = 0 }) => {
      return getStoresFn({
        ...payload,
        limit,
        skip: pageParam,
        ...(hasLocation ? { lat, lng, radius: DEFAULT_STORE_RADIUS } : {}),
      });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.stores) return undefined;
      const loaded = allPages.reduce(
        (acc, page) => acc + (page?.stores?.length || 0),
        0
      );
      return lastPage.stores.length < limit ? undefined : loaded;
    },
    // Only seed SSR data when there is NO client location: SSR list may be
    // city-based (fetched without lat/lng) and must not be shown once the user
    // has picked an address — that path must always fetch fresh nearby results.
    // In webinapp mode we also never seed (client always fetches fresh).
    initialData:
      !isWebinapp && !hasLocation && data?.length
        ? {
            pages: [{ stores: data, total: data.length }],
            pageParams: [0],
          }
        : undefined,
    // Enhanced caching for smooth navigation
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    refetchOnMount: false, // Don't refetch if we have fresh data
    refetchOnWindowFocus: false,
  });

  // Get all stores from infinite query
  const allStores = useMemo(() => {
    return (
      storesData?.pages?.flatMap((page) => page.stores).filter(Boolean) ?? []
    );
  }, [storesData?.pages]);

  const bannerConfig = useMemo(() => {
    if (!processedDeals) return { keys: [], interval: 0 };

    const keys = Object.keys(processedDeals).filter((key) =>
      key.startsWith('banners')
    );
    const interval = getBannerInterval(isMobile);

    return { keys, interval };
  }, [processedDeals, isMobile]);

  const renderStoreWithBanners = useCallback(() => {
    if (!allStores?.length || !bannerConfig.keys.length) return null;

    const items: React.ReactNode[] = [];

    for (let i = 0; i < allStores.length; i++) {
      const store = allStores[i];
      if (!store?._id) continue;

      items.push(<StoreCard key={store._id} data={store} />);

      if (shouldShowBannerAtIndex(i, bannerConfig.interval)) {
        const bannerData = getBannerData(
          i,
          bannerConfig.interval,
          bannerConfig.keys,
          processedDeals!
        );

        if (bannerData) {
          items.push(
            <div
              key={`banner-${i}`}
              className={cn(
                'col-span-full w-full overflow-hidden',
                isMobile ? 'my-4' : 'md:col-span-4 md:my-6'
              )}
            >
              <BannerSection banners={bannerData} isLoading={false} />
            </div>
          );
        }
      }
    }

    return items;
  }, [allStores, bannerConfig, processedDeals, isMobile]);

  if (!allStores?.length) {
    // Query still settling (webinapp / fresh location fetch): show skeleton
    // instead of prematurely flashing the empty state.
    if (isPending || !isFetched) {
      return (
        <div
          className={cn(
            'grid gap-4 p-4 auto-rows-max',
            'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'
          )}
        >
          <InlineLoadingSkeletons />
        </div>
      );
    }

    // User has picked an address but no stores exist within the radius:
    // show the empty state — do NOT fall back to the city-wide list.
    if (hasLocation) {
      return <StoreEmptyState variant="nearby" />;
    }

    // No client location: city-wide list (via city_id) is the correct result.
    return <StoreList data={data} payload={payload} />;
  }

  if (processedDeals && bannerConfig.keys.length > 0) {
    return (
      <div className="space-y-6">
        <InfiniteScroll
          className={cn(
            'grid gap-4 auto-rows-max items-stretch justify-items-stretch',
            'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'
          )}
          dataLength={allStores.length}
          next={fetchNextPage}
          hasMore={!!hasNextPage}
          loader={<p className="col-span-full text-center">Đang tải thêm...</p>}
          endMessage={<p className="col-span-full text-center"></p>}
          data-testid="home-store-list"
        >
          {renderStoreWithBanners()}
        </InfiniteScroll>
      </div>
    );
  }

  return (
    <div className="space-y-6 mx-auto bg-transparent">
      <InfiniteScroll
        className={cn(
          'grid gap-4 bg-transparent p-4 auto-rows-max items-stretch justify-items-stretch',
          'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'
        )}
        dataLength={allStores.length}
        next={fetchNextPage}
        hasMore={!!hasNextPage}
        loader={<p className="col-span-full text-center">Đang tải thêm...</p>}
        endMessage={<p className="col-span-full text-center"></p>}
        data-testid="home-store-list"
      >
        {allStores.map((store) => (
          <StoreCard key={store._id} data={store} />
        ))}
      </InfiniteScroll>
    </div>
  );
}
